import { useState } from 'react';
import { useNavigate } from 'react-router';
import { login, type CurrentUser } from '../../../shared/features/session/session.api';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { useToast } from '../../../shared/hooks/use-toast';
import { carriedOverEmail, shownLoginStep, type LoginStep } from '../policies/login-step.policy';
import { useLoginCodeStep } from './use-login-code-step';
import { useSignInCode } from './use-sign-in-code';
import { useSignInCodeChannels } from './use-sign-in-code-channels';

interface PasswordFormValues {
  email: string;
  password: string;
}

/**
 * A code to the email or mobile typed signs in on its own. A password is the other way in, and it is followed by the
 * account's two-factor check when one is on; it is the only way in when no channel can send a code.
 */
export function useLoginPage() {
  const navigate = useNavigate();
  const { setUser } = useCurrentUser();
  const toast = useToast();
  const channels = useSignInCodeChannels();
  const [step, setStep] = useState<LoginStep>('start');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onSignedIn(user: CurrentUser) {
    setUser(user);
    navigate('/', { replace: true });
    toast.success(`Welcome back, ${user.name.split(' ')[0]}`);
  }

  const twoFactorStep = useLoginCodeStep(onSignedIn);
  const codeStep = useSignInCode({ channels: channels ?? [], onCodeSent: () => setStep('code'), onSignedIn });

  function show(next: LoginStep) {
    setError(null);
    codeStep.clearError();
    setStep(next);
  }

  async function submitPassword(values: PasswordFormValues) {
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(values);
      if ('requiresTwoFactor' in result) {
        twoFactorStep.begin(result.methods);
        setStep('twoFactor');
        return;
      }
      onSignedIn(result);
    } catch {
      // One message mirrors the backend's non-disclosure: wrong password, unknown email and a locked-out account read alike.
      setError('Incorrect email or password.');
    } finally {
      setSubmitting(false);
    }
  }

  const isCodeOffered = Boolean(channels?.length);

  return {
    /** The channels are still being asked for, so which step comes first isn't known yet. */
    isLoading: channels === null,
    isCodeOffered,
    step: shownLoginStep(step, isCodeOffered),
    error,
    submitting,
    submitPassword,
    passwordEmail: carriedOverEmail(codeStep.typed),
    codeStep,
    twoFactorStep,
    showStart: () => show('start'),
    showPassword: () => show('password'),
  };
}

export type LoginPageState = ReturnType<typeof useLoginPage>;
